import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquareText, Smile, HeartHandshake, Sparkles, ArrowRight, Bookmark, ChevronDown, MessagesSquare, GraduationCap, BarChart3, CalendarDays, Users, TrendingUp } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import DecodeTool from "@/components/DecodeTool";

const STARTERS = [
  "Ask your teen to teach you one slang word they love — then use it (badly) on purpose.",
  "“What's a meme that made you laugh this week?” Curiosity beats questions about school.",
  "Sit beside them while they game for 10 minutes. No advice, just company.",
  "“If I don't get something you say, will you explain it instead of cringing?” Set the tone with warmth.",
];

const TOOLS = [
  { id: "decode", label: "Decode suite", icon: MessageSquareText, to: "/app/decode" },
  { id: "coach", label: "Coach & simulator", icon: MessagesSquare, to: "/app/coach" },
  { id: "learn", label: "Learn & style quiz", icon: GraduationCap, to: "/app/learn" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [children, setChildren] = useState([]);
  const [activeChild, setActiveChild] = useState(null);
  const [switcher, setSwitcher] = useState(false);
  const [recent, setRecent] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [digest, setDigest] = useState(null);
  const starter = STARTERS[new Date().getDate() % STARTERS.length];

  const load = () => {
    api.get("/children").then(({ data }) => { setChildren(data.children); if (data.children[0]) setActiveChild(data.children[0]); }).catch(() => {});
    api.get("/history").then(({ data }) => setRecent(data.items.slice(0, 4))).catch(() => {});
    api.get("/metrics").then(({ data }) => setMetrics(data)).catch(() => {});
    api.get("/digest").then(({ data }) => setDigest(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const label = (c) => c ? `${c.nickname || "Child"} · ${c.age_band}` : "All children";

  return (
    <div className="space-y-8" data-testid="dashboard-main">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-[28px] sm:text-[34px] font-semibold">Hi {user.first_name || "there"} 👋</h1>
          <p style={{ color: "var(--text-muted)" }}>Whatever came up today, let's make sense of it — calmly.</p>
        </div>
        <div className="relative">
          <button className="btn btn-outline" onClick={() => setSwitcher(!switcher)} data-testid="dashboard-child-switcher">
            {label(activeChild)} <ChevronDown size={16} />
          </button>
          {switcher && (
            <div className="absolute right-0 mt-2 w-56 dk-card p-2 z-30 anim-in">
              <button className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#E5E2DC] text-sm" onClick={() => { setActiveChild(null); setSwitcher(false); }}>All children</button>
              {children.map((c) => (
                <button key={c.id} className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#E5E2DC] text-sm" onClick={() => { setActiveChild(c); setSwitcher(false); }} data-testid={`switch-child-${c.id}`}>{label(c)}</button>
              ))}
              <button className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#E5E2DC] text-sm link" onClick={() => nav("/app/children")}>Manage family →</button>
            </div>
          )}
        </div>
      </div>

      {metrics && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="dashboard-metrics">
          <Stat icon={BarChart3} label="Total decodes" value={metrics.total_decodes} />
          <Stat icon={Bookmark} label="Saved" value={metrics.saved} />
          <Stat icon={CalendarDays} label="Active days" value={metrics.active_days} />
          <Stat icon={Users} label="Children" value={metrics.children} />
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 dk-card p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Sparkles size={18} style={{ color: "var(--accent)" }} />
            <h2 className="font-serif text-[20px] font-medium">Quick decode</h2>
          </div>
          <DecodeTool tool="text" childId={activeChild?.id} onSaved={load} />
          <button className="link text-sm flex items-center gap-1" onClick={() => nav("/app/decode")}>Open the full Decode Suite <ArrowRight size={14} /></button>
        </div>

        <div className="space-y-6">
          <div className="dk-card p-6" style={{ background: "var(--sage-tint)" }}>
            <span className="text-xs uppercase tracking-wide" style={{ color: "var(--sage)" }}>Starter of the day</span>
            <p className="font-serif text-[18px] mt-2">{starter}</p>
          </div>

          {digest && digest.items?.length > 0 && (
            <div className="dk-card p-6" data-testid="weekly-trends-card">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp size={16} style={{ color: "var(--accent)" }} />
                <h3 className="font-sans font-semibold">This week's trends</h3>
                <button className="link text-xs ml-auto" onClick={() => nav("/app/trends")} data-testid="trends-see-all">See all →</button>
              </div>
              <p className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>{digest.intro}</p>
              <ul className="space-y-2">
                {digest.items.slice(0, 5).map((it, i) => (
                  <li key={i} className="rounded-lg p-3" style={{ background: "var(--card-alt)" }} data-testid="trend-item">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{it.term}</span>
                      <span className={`badge-soft pill-${it.vibe || "green"}`}>{(it.vibe || "green") === "green" ? "chill" : (it.vibe === "red" ? "check in" : "notice")}</span>
                    </div>
                    <div className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>{it.note}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="dk-card p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-sans font-semibold">Recent decodes</h3>
              <Bookmark size={16} style={{ color: "var(--text-muted)" }} />
            </div>
            {recent.length === 0 ? (
              <p className="text-sm" style={{ color: "var(--text-muted)" }}>No decodes yet — your history will appear here.</p>
            ) : (
              <ul className="space-y-2">
                {recent.map((r) => (
                  <li key={r.id} className="rounded-lg p-3 text-sm" style={{ background: "var(--card-alt)" }} data-testid="recent-item">
                    <div className="font-medium truncate">{r.result?.term || r.input}</div>
                    <div className="text-xs truncate" style={{ color: "var(--text-muted)" }}>{r.result?.meaning}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div>
        <h2 className="font-serif text-[22px] font-medium mb-4">Jump into a tool</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          {TOOLS.map((t) => (
            <button key={t.id} className="dk-card p-6 text-left hover:border-[#C85A48] transition-colors" onClick={() => nav(t.to)} data-testid={`dashboard-tool-${t.id}`}>
              <span className="grid place-items-center w-11 h-11 rounded-xl mb-3" style={{ background: "var(--sage-tint)" }}><t.icon style={{ color: "var(--sage)" }} /></span>
              <h3 className="font-serif text-[18px] font-medium">{t.label}</h3>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="dk-card p-4 flex items-center gap-3" data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
      <span className="grid place-items-center w-10 h-10 rounded-xl shrink-0" style={{ background: "var(--sage-tint)" }}><Icon size={18} style={{ color: "var(--sage)" }} /></span>
      <div>
        <div className="font-serif text-[24px] font-semibold leading-none">{value ?? 0}</div>
        <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{label}</div>
      </div>
    </div>
  );
}
